// pages/Homepage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Redesigned homepage using the newSiaraProperties design system.
//
// Child component mapping (old → new):
//   Banner + Search              → <Hero />
//   RecentlyPosted               → <PropertySection label="New Listings"     title="Recently" titleEm="Posted" />
//   RentProperty                 → <PropertySection label="For Rent"         title="Rent"     titleEm="Properties" />
//   Suggestions                  → <PropertySection label="Suggestions"      title="You May"  titleEm="Like" />
//   MostViewed                   → <PropertySection label="Trending"         title="Most"     titleEm="Viewed" />
//   Poshhouse                    → <PropertySection label="Premium Collection" title="Posh"   titleEm="House" />
//   CommercialProperty           → <PropertySection label="Business Spaces"  title="Commercial" titleEm="Properties" />
//   Pricedrop                    → <PropertySection label="Best Deals"       title="Price"    titleEm="Drop"  priceDrop />
//   Luxuaryhouse                 → <PropertySection label="Elite Living"     title="Luxury"   titleEm="House" />
//
// Each section makes its OWN API call via the useSectionData hook so the
// original API surface is unchanged. Just update the endpoint strings below.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Hero from "../components/UpdatedCode/Hero";
import MarqueeStrip from "../components/UpdatedCode/MarqueeStrip";
import WelcomeSection from "../components/UpdatedCode/WelcomeSection";
import PropertyTypes from "../components/UpdatedCode/PropertyTypes";
import StatsStrip from "../components/UpdatedCode/StatsStrip";
import PropertySection from "../components/UpdatedCode/PropertySection";
// import Footer from "../components/UpdatedCode/Footer";

import axios from "axios";
import useLenis from "../hooks/useLenis";

gsap.registerPlugin(ScrollTrigger);

// ── shared images (used across all sections) ──
function useAllImages() {
  const [images, setImages] = useState(null);
  useEffect(() => {
    axios
      .get("/api/property/getAllPropertyImages")
      .then((r) => setImages(r.data))
      .catch(() => setImages({ data: [] }));
  }, []);
  return images;
}

// ── per-section data hook ──
function useSectionData(endpoint) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(endpoint)
      .then((r) => setData(r.data))
      .catch(() => setData({ data: [] }));
  }, [endpoint]);
  return data;
}

export default function HomePageNew() {
  const location = useLocation();

  // Initialize Lenis smooth scroll
  useLenis();

  // Analytics + scroll-to-top
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Scroll reveal animation for elements with class '.reveal'
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none none",
            },
          },
        );
      });
    });
    return () => ctx.revert();
  }, []);

  // ── shared images ──
  const images = useAllImages();

  // ── individual section data ──
  const recentlyPosted = useSectionData(
    "/api/property/getRecentlyPostedProperties",
  );
  const rentProperties = useSectionData("/api/property/getPropertyForRent");
  const suggestions = useSectionData("/api/property/getSuggestedProperty");
  const mostViewed = useSectionData("/api/property/getMostVisitedProperties");
  const poshHouse = useSectionData("/api/property/getPropertyByType/villa");
  const commercial = useSectionData(
    "/api/property/getPropertyByType/commercial",
  );
  const priceDrop = useSectionData("/api/property/getAllProperty");
  const luxuryHouse = useSectionData(
    "/api/property/getPropertyByType/farmHouse",
  );

  return (
    <Wrapper>
      <Helmet>
        <title>
          Property Dealer in Jabalpur | Buy, Sell & Rent - Siara Properties
        </title>
        <meta
          name="description"
          content="Siara Properties – top property dealer in Jabalpur for residential & commercial properties. Genuine listings, best prices, trusted service. Enquire now!"
        />
        <link rel="canonical" href="https://siaraproperties.com/" />
        <meta
          property="og:title"
          content="Property Dealer in Jabalpur | Buy, Sell & Rent - Siara Properties"
        />
        <meta
          property="og:description"
          content="Siara Properties – top property dealer in Jabalpur for residential & commercial properties. Genuine listings, best prices, trusted service. Enquire now!"
        />
        <meta property="og:url" content="https://siaraproperties.com/" />
      </Helmet>

      {/* ── NAVIGATION ── */}

      {/* ── HERO (Banner + Search) ── */}
      <Hero />

      {/* ── MARQUEE ── */}
      <MarqueeStrip />

      {/* ── WELCOME SECTION ── */}
      <WelcomeSection />

      {/* ── EXPLORE PROPERTY TYPES ── */}
      <PropertyTypes />

      {/* ── STATS ── */}
      <StatsStrip />

      {/* ── PROPERTY SECTIONS ── */}
      <PropertySection
        label="New Listings"
        title="Recently"
        titleEm="Posted"
        properties={recentlyPosted}
        images={images}
      />

      <PropertySection
        label="For Rent"
        title="Rent"
        titleEm="Properties"
        viewAllLink="/property/propertiesForRent"
        properties={rentProperties}
        images={images}
      />

      <PropertySection
        label="Suggested for You"
        title="You May"
        titleEm="Like"
        properties={suggestions}
        images={images}
      />

      <PropertySection
        label="Trending"
        title="Most"
        titleEm="Viewed"
        properties={mostViewed}
        images={images}
      />

      <PropertySection
        label="Premium Collection"
        title="Posh"
        titleEm="House"
        viewAllLink="/property/propertyType/villa"
        properties={poshHouse}
        images={images}
        emptyText="No posh properties available at the moment."
      />

      <PropertySection
        label="Business Spaces"
        title="Commercial"
        titleEm="Properties"
        viewAllLink="/property/propertyType/commercial"
        properties={commercial}
        images={images}
        emptyText="No commercial properties available at the moment."
      />

      <PropertySection
        label="Best Deals"
        title="Price"
        titleEm="Drop"
        properties={priceDrop}
        images={images}
        priceDrop={true}
        bg="var(--terra-light, #F5EDE3)"
      />

      <PropertySection
        label="Elite Living"
        title="Luxury"
        titleEm="House"
        viewAllLink="/property/propertyType/farmHouse"
        properties={luxuryHouse}
        images={images}
        emptyText="No luxury houses available at the moment."
      />

      {/* ── FOOTER ──
      <Footer /> */}
    </Wrapper>
  );
}

// ─── GLOBAL STYLES for this page ─────────────────────────────────────────────
const Wrapper = styled.div`
  /* push content below fixed navbar */
  padding-top: 64px;
  overflow-x: hidden;
  width: 100%;

  /* CSS variables available to all children */
  --cream: #faf8f3;
  --warm-white: #fffef9;
  --sand: #f0ebe1;
  --sand-dark: #e2d9cc;
  --terra: #b8895a;
  --terra-dark: #8c6340;
  --terra-light: #f5ede3;
  --sage: #7a8c6e;
  --charcoal: #2c2c28;
  --charcoal-mid: #5a5a54;
  --charcoal-light: #9a9a94;
  --gold: #c9a84c;
  --gold-light: #f7f0dc;

  font-family: "DM Sans", sans-serif;
  background: var(--cream);
  color: var(--charcoal);

  /* scroll-reveal base class used by GSAP */
  .reveal {
    opacity: 0;
    transform: translateY(32px);
  }
`;
